/**
 * The colours a chart is drawn in.
 *
 * Sampled from the application's own backdrop -- the seven-stop gradient in
 * `index.css` -- so a chart is coloured out of the same sweep the page behind it
 * is, cool through warm. The stops are mirrored here because a CSS gradient is
 * not readable from script; keep the two in step.
 *
 * The backdrop's stops are deliberately pale, being a half-opacity wash. A chart
 * mark has to carry meaning at the size of a pie slice, so each hue is taken to
 * a fixed mid lightness and a stronger chroma while its hue is left alone -- the
 * sweep survives, the paleness does not.
 *
 * These are `oklch()` strings, which is the whole of the colour handling here.
 * The Vue application converts every stop through RGB to a hex triple, because
 * `chart.js` paints onto a canvas and needs a concrete colour; an SVG chart
 * takes whatever CSS understands, and CSS understands this.
 */

/** Lightness, chroma and hue in degrees, matching the gradient in `index.css`. */
interface Stop {
  readonly l: number
  readonly c: number
  readonly h: number
}

const GRADIENT: readonly Stop[] = [
  { l: 0.81851, c: 0.04418, h: 240.912 },
  { l: 0.8389, c: 0.02099, h: 279.841 },
  { l: 0.86684, c: 0.02829, h: 9.254 },
  { l: 0.90133, c: 0.05237, h: 35.636 },
  { l: 0.91519, c: 0.05213, h: 56.711 },
  { l: 0.93196, c: 0.05553, h: 74.914 },
  { l: 0.95009, c: 0.05767, h: 87.561 },
]

const MARK_LIGHTNESS = 0.64
const CHROMA_SCALE = 2.6
const CHROMA_MAX = 0.16

/**
 * The gradient at a point along it, hue interpolated the short way round.
 *
 * Going the long way round the wheel would take the sweep from blue to amber
 * back through green, which is a different gradient from the one on the page.
 */
function sample(position: number): Stop {
  const clamped = Math.min(Math.max(position, 0), 1)
  const scaled = clamped * (GRADIENT.length - 1)
  const lower = Math.floor(scaled)
  const from = GRADIENT[lower] ?? GRADIENT[0]!
  const to = GRADIENT[Math.min(lower + 1, GRADIENT.length - 1)] ?? from
  const fraction = scaled - lower

  let hueStep = to.h - from.h
  if (hueStep > 180) hueStep -= 360
  if (hueStep < -180) hueStep += 360

  return {
    l: from.l + (to.l - from.l) * fraction,
    c: from.c + (to.c - from.c) * fraction,
    h: (from.h + hueStep * fraction + 360) % 360,
  }
}

function mark({ c, h }: Stop): string {
  const chroma = Math.min(c * CHROMA_SCALE, CHROMA_MAX)
  return `oklch(${MARK_LIGHTNESS} ${chroma.toFixed(4)} ${h.toFixed(3)})`
}

/**
 * One colour per category, spread across the whole sweep.
 *
 * Spread rather than cycled through a fixed palette, so three categories are as
 * far apart as they can be rather than being the first three of seven. One
 * category takes the first colour, since there is nothing to be far from.
 */
export function categoricalColors(count: number): string[] {
  if (count <= 0) return []
  if (count === 1) return [mark(sample(0))]

  return Array.from({ length: count }, (_, index) => mark(sample(index / (count - 1))))
}

/** The one colour a single-series chart is drawn in: the cool end of the sweep. */
export function primaryColor(): string {
  return mark(sample(0))
}
